import { useRouter } from 'next/router';
import type { SelectedFilterValue } from '../types';
import type { SelectedFilter } from '../api/rest/types';
import {
  decodeSelectedFilterValues,
  decompressSelectedFilterValues,
  compressSelectedFilterValues,
  encodeSelectedFilterValues,
  pruneQuery,
  encodePage,
  decodePage,
  encodeCategoryFilter,
} from '../queryEncoding';
import { seoFilterSeparator } from '../utils/categoryPaths';
import { isFullyContained, mergeSelectedFilterValues } from '../utils/selectedFilterValues';
import { useSeoFilters } from './useSeoFilters';
import { useSpecialCategoryFilter } from './useSpecialCategoryFilter';

/**
 * Use selected filters persisted in the url query
 *
 * Changing filters results in a router.push, which will trigger a data reload
 */
export const useFilters = (selectedFilters: SelectedFilter[]) => {
  const {
    push,
    pathname,
    query: { categories: currentCategories, ...restOfQuery },
  } = useRouter();

  const { selectedSeoFilterValues } = useSeoFilters(selectedFilters);
  const { selectedCategoryFilterValues } = useSpecialCategoryFilter(selectedFilters);

  const selectedFilterValuesFromQuery = decompressSelectedFilterValues(
    decodeSelectedFilterValues(restOfQuery),
    selectedFilters,
  );

  const selectedFilterValues = mergeSelectedFilterValues(
    selectedFilterValuesFromQuery,
    selectedSeoFilterValues.concat(selectedCategoryFilterValues),
  );

  const pushFilterChange = (values: SelectedFilterValue[]) => {
    const seoFilterIsStillActive = selectedSeoFilterValues.every((value) =>
      values.some((newValue) => isFullyContained(value, newValue)),
    );

    const isSeoFilterRoute = currentCategories?.includes(seoFilterSeparator);

    const serpCategoryFilterValue = values.find((value) =>
      selectedFilters.some((filter) => filter.id === value.filterId && filter.isCategoryFilter),
    );

    const otherValues = values.filter((value) => value !== serpCategoryFilterValue);

    // remove SEO filter from route unless the SEO filter is still fully selected
    const categories =
      isSeoFilterRoute && !seoFilterIsStillActive
        ? currentCategories?.slice(0, currentCategories?.indexOf(seoFilterSeparator))
        : currentCategories;

    const { pagingMode } = decodePage(restOfQuery);

    const query = pruneQuery({
      ...restOfQuery,
      ...encodeSelectedFilterValues(compressSelectedFilterValues(otherValues)),
      ...(pagingMode !== 'infinite' && encodePage()), // every filter change resets page counter unless infinite scrolling is active
      categories,
      // always overwrite any existing category filter, to make sure it can be properly removed
      ...encodeCategoryFilter(serpCategoryFilterValue?.selectedValues?.[0]),
    });

    push(
      {
        // currently the /c/ prefix is internal-only, and rewritten in the middleware
        pathname: pathname.replace(/^\/c/, '').concat('/'),
        query,
      },
      undefined,
      { scroll: false },
    );
  };

  return {
    selectedFilterValues,
    pushFilterChange,
  };
};
